import { Link, instructorLinkState, useSharedAvatarStyle } from '@/components/layout/TransitionLink'
import { motion } from 'motion/react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { initials } from '@/lib/format'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { EASE } from '@/components/dashboard/fx/motion'

export interface RankedEntry {
  id: string
  name: string
  image_url: string | null
  count: number
}

interface RankedListProps {
  entries: RankedEntry[]
  unit: string
  linkTo?: (entry: RankedEntry) => string
}

// Oversized outline numerals, a bar that grows to each row's share of the
// leader, and a portrait that colors in on hover.
// Hook-bearing so a linked row's avatar can carry a view-transition name.
function RankedAvatar({ entry, to }: { entry: RankedEntry; to?: string }) {
  const style = useSharedAvatarStyle(to ?? '', entry.id, Boolean(to))
  return (
    <Avatar
      style={style}
      className="size-12 shrink-0 grayscale transition duration-500 group-hover:scale-110 group-hover:grayscale-0"
    >
      <AvatarImage src={entry.image_url ?? undefined} alt="" />
      <AvatarFallback className="text-xs">{initials(entry.name)}</AvatarFallback>
    </Avatar>
  )
}

export function RankedList({ entries, unit, linkTo }: RankedListProps) {
  const max = Math.max(...entries.map((e) => e.count), 1)

  return (
    <ol>
      {entries.map((entry, i) => {
        const body = (
          <>
            <span
              className={`w-[3.25rem] shrink-0 font-display text-5xl font-semibold leading-none tracking-tighter transition-colors sm:w-16 sm:text-6xl ${
                i === 0 ? 'text-gradient' : 'text-outline group-hover:text-foreground'
              }`}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <RankedAvatar entry={entry} to={linkTo?.(entry)} />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-lg font-medium sm:text-xl">{entry.name}</span>
                <span className="shrink-0 text-sm text-muted-foreground">
                  <CountUp value={entry.count} className="font-medium text-foreground" /> {unit}
                </span>
              </div>
              <div className="h-[3px] w-full overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="h-full origin-left rounded-full bg-gradient-to-r from-primary to-[#e87ba4]"
                  style={{ width: '100%' }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: entry.count / max }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, delay: 0.15 + i * 0.05, ease: EASE }}
                />
              </div>
            </div>
          </>
        )

        const rowClass =
          'group flex items-center gap-3 border-b border-border/60 py-4 transition-[padding] duration-300 hover:pl-3 sm:gap-4'

        return (
          <li key={entry.id}>
            <Reveal delay={i * 0.05} y={24}>
              {linkTo ? (
                <Link to={linkTo(entry)} state={instructorLinkState(entry.name, entry.image_url)} className={rowClass}>
                  {body}
                </Link>
              ) : (
                <div className={rowClass}>{body}</div>
              )}
            </Reveal>
          </li>
        )
      })}
    </ol>
  )
}
